<template>
  <template v-for="item in navItems" v-bind:key="item.name">
    <RouterLink
      v-if="!item.hasOwnProperty('subItems')"
      :to="item.path"
      custom
      v-slot="{ href, navigate, isActive }"
    >
      <a
        :href="href"
        :class='["item",{"mobile": mobile, "desktop": desktop, "active": isActive}]'
        :data-testid="linkTestId(item.path)"
        @click="navigate"
      >
        {{ item.name }}
      </a>
    </RouterLink>
    <Menu
      v-else-if="item.hasOwnProperty('subItems')"
      :items='item.subItems'
      :activatorTestId="activatorTestId(item)"
      :toggleTestId="toggleTestId(item)"
      :attachItemTestIds="!!desktop"
      :itemTestIdPrefix="testIdPrefix"
    >
      <template #expander>
        <div :class='["item",{"mobile": mobile, "desktop": desktop}]'>
          {{ item.name }}
        </div>
      </template>
    </Menu>
  </template>
</template>

<script lang="ts" setup>
import { reactive } from 'vue';
import { RouterLink } from 'vue-router';
import Menu from '@/components/Menu.vue';
import navItemsData from './navItems.config';
import useUser from '@/composables/user';

const user = useUser();

const props = defineProps<{
  mobile?: boolean;
  desktop?: boolean;
}>();

/** Desktop: desktop-href*; mobile: nav-* — avoids duplicate ids when both menus are in the DOM. */
const testIdPrefix = props.desktop ? 'desktop-href' : 'nav-';

function pathSlug(path?: string) {
  if (!path) return undefined;
  let slug = String(path).replace(/^\//, '').replace(/\//g, '-') || 'home';
  // Requested desktop id is desktop-hrefplayer (singular); keep mobile as nav-players.
  if (props.desktop && slug === 'players') slug = 'player';
  return slug;
}

function linkTestId(path?: string) {
  const slug = pathSlug(path);
  return slug ? `${testIdPrefix}${slug}` : undefined;
}

function activatorTestId(item: any) {
  if (item.path) return linkTestId(item.path);
  if (item.name === 'Settings') return `${testIdPrefix}settings`;
  if (item.name === 'Analytics') return `${testIdPrefix}analytics`;
  return undefined;
}

function toggleTestId(item: any) {
  if (item.name === 'Settings') return `${testIdPrefix}settings-toggle`;
  return undefined;
}

const navItemsFiltered = navItemsData
  .filter(filterNavItem)
  .map(withNavSubItemsFiltered);

const navItems = reactive(navItemsFiltered);

function filterNavItem(navItem) {
  return isUserHasAccessToNavItem(navItem)
    && ( !navItem.checkIsVisible || (navItem.checkIsVisible && navItem.checkIsVisible(user)) );
    // TODO: consider using a more meaningful name instead of checkIsVisible, may be userHasAccessToNavItem or hasAccessCallback
}

function withNavSubItemsFiltered(navItem) {
  // Return navItem if it doesn't have subItems
  if(!navItem.hasOwnProperty('subItems')) {
    return navItem;
  }

  // Return navItem with prepared subitems
  const subItemsFiltered = navItem.subItems.filter(filterNavItem);
  return {
    ...navItem,
    subItems: subItemsFiltered,
  }
}

function isUserHasAccessToNavItem(navItem) {
  const isHasAccess = (
    (navItem.hasOwnProperty('areaAccessName') &&
      user.hasReadAccessToArea(navItem.areaAccessName)) ||
    !navItem.hasOwnProperty('areaAccessName')
  )
  return isHasAccess;
}
</script>

<style lang="scss" scoped>
.item.desktop {
  font-size: 16px;
  margin-right: 24px;
  letter-spacing: 0.9px;
  color: #25294A;
  text-decoration: none;
  cursor: pointer;
  @include medium-devices-down {
    font-size: 0.8em;
  }
  
  &:hover {
    font-weight: bold;
  }
  &.active {
    text-decoration: underline;
    text-decoration-color: #FFFFFF;
    text-underline-position: under;
    font-weight: bold;
  }
}



.item.mobile {
  text-decoration: none;
  color: #ffffffbf;
  margin: 0.3em;
  font-size: 2em;
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover, &.active {
    font-weight: bold;
    color: #fffffff2;
  }

  &.active {
    text-decoration: underline;
    text-decoration-color: #ffffff64;
    text-underline-position: under;
  }
}
</style>